import { useState, useEffect, useRef } from 'react';
import { Car, supabase, getCarGradient } from '../lib/supabase';
import { Plus, Car as CarIcon, GripVertical } from 'lucide-react';
import { NavigationBar } from '../components/ui/NavigationBar';
import { CompactCarCard } from '../components/cars/CompactCarCard';
import { theme, styles } from '../lib/theme';
import { useAuth } from '../contexts/AuthContext';
import { dataManager } from '../lib/dataManager';
import { useTranslations } from '../i18n';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { usePreferences } from '../contexts/PreferencesContext';
import { getCarLimitStatus } from '../lib/carLimits';
import { AlertModal } from '../components/ui/AlertModal';
import { useNavigate } from 'react-router-dom';

type CarsListProps = {
  cars: Car[];
  onBack: () => void;
  onEditCar: (car: Car) => void;
  onAddCar: () => void;
};

type CarWithStats = Car & {
  totalExpenses: number;
  expenseCount: number;
  latestMileage: number | null;
};

export function CarsList({ cars, onBack, onEditCar, onAddCar }: CarsListProps) {

  const { user } = useAuth();
  const { t } = useTranslations();
  const { preferences } = usePreferences();
  const navigate = useNavigate();
  const [carsWithStats, setCarsWithStats] = useState<CarWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [carPreferences, setCarPreferences] = useState<Record<string, any>>({});
  const [limitAlertOpen, setLimitAlertOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Restore scroll position
  useScrollRestoration(scrollContainerRef);

  useEffect(() => {
    loadData();
  }, [cars]);

  const loadData = async () => {
    if (cars.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    await Promise.all([loadCarStats(), loadCarPreferences()]);
    setLoading(false);
  };

  const loadCarStats = async () => {
    const statsPromises = cars.map(async (car) => {
      const [expensesResult, mileageResult] = await Promise.all([
        supabase
          .from('expenses')
          .select('amount, is_initial_purchase')
          .eq('car_id', car.id),
        supabase
          .from('log_entries')
          .select('mileage')
          .eq('car_id', car.id)
          .gt('mileage', 0)
          .order('date', { ascending: false })
          .limit(1)
          .maybeSingle()
      ]);

      // Calculate total expenses, but keep track of the initial purchase one
      // so we don't double count if we also use car.purchase_price
      let repairExpenses = 0;
      let handledPurchasePrice = 0;

      expensesResult.data?.forEach(e => {
        const amt = e.amount ? parseFloat(e.amount.toString()) : 0;
        if (e.is_initial_purchase) {
          handledPurchasePrice = amt;
        } else {
          repairExpenses += amt;
        }
      });

      const totalExpenses = repairExpenses + (handledPurchasePrice || car.purchase_price || 0);
      const expenseCount = expensesResult.data?.length || 0;
      const latestMileage = mileageResult.data?.mileage || null;

      // Overall Result = Sale Price - (Purchase Price + Other Expenses)
      // This gives the net profit or loss
      let overallResult = null;
      if (car.is_archived && car.sell_price !== null && car.sell_price !== undefined) {
        const purchase = car.purchase_price || handledPurchasePrice || 0;
        overallResult = car.sell_price - (purchase + repairExpenses);
      }

      return {
        ...car,
        totalExpenses,
        expenseCount,
        latestMileage,
        overallResult
      };
    });

    const stats = await Promise.all(statsPromises);

    // Fetch favorite counts for all cars in one go
    const carIds = cars.map(c => c.id);
    const { data: favCounts } = await supabase.rpc('get_cars_favorite_counts', {
      p_car_ids: carIds
    });

    // Create a map of car_id -> favorite_count
    const favCountsMap: Record<string, number> = {};
    if (favCounts) {
      favCounts.forEach((fc: any) => {
        favCountsMap[fc.car_id] = fc.favorite_count;
      });
    }

    // Merge favorite counts into stats
    const statsWithFavCounts = stats.map(s => ({
      ...s,
      favorite_count: favCountsMap[s.id] || 0
    }));

    setCarsWithStats(statsWithFavCounts);
  };

  const loadCarPreferences = async () => {
    const userIds = Array.from(new Set(cars.map(c => c.user_id)));
    try {
      const prefs = await Promise.all(userIds.map(id => dataManager.fetchUserPreferences(id)));
      const prefsMap: Record<string, any> = {};
      userIds.forEach((id, index) => {
        prefsMap[id] = prefs[index];
      });
      setCarPreferences(prefsMap);
    } catch (err) {
      console.error('Error loading car preferences for list:', err);
    }
  };

  // Car limit check
  const limitStatus = getCarLimitStatus(cars, preferences);

  const handleAddCarClick = () => {
    if (limitStatus.isReached) {
      setLimitAlertOpen(true);
    } else {
      onAddCar();
    }
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedItem(id);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires dataTransfer data
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;

    const currentOrder = [...carsWithStats];
    const draggedIndex = currentOrder.findIndex(c => c.id === draggedItem);
    const targetIndex = currentOrder.findIndex(c => c.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Swap items in local state for visual feedback
    const newOrder = [...currentOrder];
    const [removed] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, removed);

    // Update temporary sort orders
    newOrder.forEach((c, index) => {
      c.sort_order = index;
    });

    setCarsWithStats(newOrder);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedItem || !user) return;

    // Get final order IDs
    const finalOrderIds = carsWithStats.map(c => c.id);

    // Save to DB
    await dataManager.reorderCars(user.id, finalOrderIds);
    setDraggedItem(null);
  };

  const planLabel = preferences?.plan_type === 'pro'
    ? (t.common.planProTier || 'Pro')
    : (t.common.planFreeTier || 'Free');

  const limitAlertMessage = (t.cars.carLimitReachedMessage || '')
    .replace('{plan}', planLabel)
    .replace('{limit}', String(limitStatus.limit))
    .replace('{current}', String(limitStatus.current));

  // Categorize cars
  const nonArchived = carsWithStats.filter(c => !c.is_archived).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const myCars = nonArchived.filter(c => c.user_id === user?.id);
  const sharedCars = nonArchived.filter(c => c.user_id !== user?.id);
  const favoriteCars = nonArchived.filter(c => c.is_favorite && c.user_id !== user?.id);
  const archivedCars = carsWithStats.filter(c => c.is_archived).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div className="fixed inset-0 flex flex-col h-full bg-transparent overflow-hidden">
      <NavigationBar
        title={t.cars.title}
        onBack={onBack}
        rightAction={{
          icon: Plus,
          onClick: handleAddCarClick
        }}
      />

      <div
        ref={scrollContainerRef}
        className={`flex-1 overflow-y-auto no-scrollbar pt-20 pb-32 ${theme.spacing.pagePadding} w-full space-y-8`}
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : cars.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className={`${theme.colors.cardBg} rounded-[3rem] shadow-xl dark:shadow-none flex items-center justify-center mb-8 relative border ${theme.colors.border}`}>
              <CarIcon size={48} className="text-blue-600 dark:text-blue-400 relative z-10" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-3 tracking-tight">{t.cars.noCars}</h2>
            <p className="text-gray-500 font-medium max-w-[240px] leading-relaxed mb-10">
              {t.cars.addFirstCarNote}
            </p>
            <button
              onClick={handleAddCarClick}
              className="flex items-center gap-2 text-blue-600 font-black text-sm uppercase tracking-widest bg-blue-50 px-8 py-4 rounded-2xl hover:bg-blue-100 transition-all active:scale-95 shadow-sm shadow-blue-100"
            >
              <span>{t.cars.addVehicle}</span>
              <Plus size={16} />
            </button>
          </div>
        ) : (
          <>
            {/* Favorites Section */}
            {favoriteCars.length > 0 && (
              <div className="space-y-4">
                <h3 className={`${theme.typography.sectionTitle} px-2 flex items-center gap-2`}>
                  {t.cars.favorites}
                </h3>
                <div className={`${styles.card} ${theme.colors.divider} divide-y overflow-hidden w-full`}>
                  {favoriteCars.map((car) => (
                    <div key={`fav-${car.id}`} className="relative bg-transparent">
                      <CompactCarCard
                        car={car}
                        gradient={getCarGradient(car.placeholder_hue)}
                        image={car.use_image_icon && car.image_url ? car.image_url : undefined}
                        onClick={() => onEditCar(car)}
                        preferencesOverride={carPreferences[car.user_id]}
                        starOnRight={true}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* My Cars Section */}
            {myCars.length > 0 && (
              <div className="space-y-4">
                <h3 className={`${theme.typography.sectionTitle} px-2`}>{t.cars.myVehicles}</h3>
                <div className={`${styles.card} ${theme.colors.divider} divide-y overflow-hidden w-full`}>
                  {myCars.map((car) => (
                    <div
                      key={car.id}
                      className={`relative flex items-center transition-colors w-full ${draggedItem === car.id ? 'bg-blue-50/50 dark:bg-blue-900/20 z-10' : 'bg-transparent'}`}
                      onDragOver={(e) => handleDragOver(e, car.id)}
                      onDrop={handleDrop}
                    >
                      <div
                        className="pl-4 pr-1 py-4 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors"
                        draggable
                        onDragStart={(e) => handleDragStart(e, car.id)}
                      >
                        <GripVertical size={20} />
                      </div>
                      <div className="flex-1 w-full min-w-0">
                        <CompactCarCard
                          car={car}
                          gradient={getCarGradient(car.placeholder_hue)}
                          image={car.use_image_icon && car.image_url ? car.image_url : undefined}
                          onClick={() => onEditCar(car)}
                          preferencesOverride={carPreferences[car.user_id]}
                          hideFavoriteIcon={true}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shared With Me Section */}
            {sharedCars.length > 0 && (
              <div className="space-y-4">
                <h3 className={`${theme.typography.sectionTitle} px-2`}>{t.cars.sharedWithMe}</h3>
                <div className={`${styles.card} ${theme.colors.divider} divide-y overflow-hidden w-full`}>
                  {sharedCars.map((car) => (
                    <div key={car.id} className="relative bg-transparent">
                      <CompactCarCard
                        car={car}
                        gradient={getCarGradient(car.placeholder_hue)}
                        image={car.use_image_icon && car.image_url ? car.image_url : undefined}
                        onClick={() => onEditCar(car)}
                        preferencesOverride={carPreferences[car.user_id]}
                        hideFavoriteIcon={!car.is_favorite}
                        starOnRight={true}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Archived Section */}
            {archivedCars.length > 0 && (
              <div className="space-y-4 opacity-75">
                <h3 className={`${theme.typography.sectionTitle} px-2`}>{t.cars.archivedCars}</h3>
                <div className={`${styles.card} ${theme.colors.divider} divide-y overflow-hidden w-full`}>
                  {archivedCars.map((car) => (
                    <div key={car.id} className="relative opacity-60 hover:opacity-100 transition-opacity">
                      <CompactCarCard
                        car={car}
                        gradient={getCarGradient(car.placeholder_hue)}
                        image={car.use_image_icon && car.image_url ? car.image_url : undefined}
                        onClick={() => onEditCar(car)}
                        preferencesOverride={carPreferences[car.user_id]}
                        hideFavoriteIcon={true}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Car limit alert */}
      <AlertModal
        isOpen={limitAlertOpen}
        title={t.cars.carLimitReachedTitle || 'Car Limit Reached'}
        message={limitAlertMessage}
        confirmLabel={t.common.done}
        onClose={() => setLimitAlertOpen(false)}
        secondaryAction={{
          label: t.cars.carLimitGoToSettings || 'Go to Settings',
          onClick: () => { onBack(); navigate('/settings'); },
        }}
      />
    </div>
  );
}
